/* eslint-disable react/sort-comp */
import Octicon, { Dash, Plus, Question } from "@githubprimer/octicons-react";
import * as _ from "lodash";
import * as React from "react";
import { CSVLink } from "react-csv";
import { Collapse, Popover, PopoverHeader, PopoverBody } from "reactstrap";
import ReactFileReader from "./ReactFileReader";
import CsvValidation from "../models/CsvValidation";
import ICsvData from "../models/ICsvData";
import RowValidation from "../models/RowValidation";
import * as CsvHelper from "../utils/CsvHelpers";
import ExampleCsvPopover from "./ExampleCsvPopover";

// show error message only if there is any
function showErrorMessage(errors: ICsvData[]): string {
  return errors.length === 0 ? "d-none" : "";
}

// get the main error message
function getErrorCountMessage(errors: ICsvData[]): string {
  const errorCount = errors.length;

  if (errorCount === 0) {
    return "";
  }

  const errorStatus =
    errors[0].validation === RowValidation.Error ? "error" : "warning";
  return `${errorCount} ${errorStatus}${errorCount > 1 ? "s" : ""} found`;
}

// get the detail error messages
function getErrorMessages(errors: ICsvData[]) {
  return _.map(errors, (error) => {
    let rowNumber = "";
    if (!_.isNil(error.row)) {
      rowNumber = `Row ${error.row}: `;
    }
    return <li key={error.id}>{rowNumber + _.join(error.messages, " / ")}</li>;
  });
}

// hide button and show file info after the csv file uploaded
function getButtonStyle(isValid: CsvValidation, isBtn: boolean): string {
  switch (isValid) {
    case CsvValidation.Unknown:
      return isBtn ? "" : "d-none";
    default:
      return isBtn ? "d-none" : "";
  }
}

interface IProps {
  uploadCsv: (csvData: ICsvData[], isValid: CsvValidation) => void;
}

interface IState {
  collapse: boolean;
  csvData: ICsvData[];
  fileName: string;
  isValid: CsvValidation;
  showPopover: boolean;
}

class CsvUploader extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      collapse: false,
      csvData: [],
      fileName: "",
      isValid: CsvValidation.Unknown,
      showPopover: false,
    };
  }

  // to fix bug with remove button on Firefox and Edge
  public removeFile = () => {
    window.location.reload();
  };

  public render() {
    const { csvData, fileName, isValid, collapse, showPopover } = this.state;

    const errors = CsvHelper.getRowErrors(csvData);
    const collapseIcon = collapse ? Dash : Plus;

    return (
      <div>
        <div className="csv-uploader container">
          <p>
            {"Calculate the inbreeding percentage for potential matings to "}
            {"help you reduce the risks of inbreeding in your herd."}
            {<br />}
            {"Ideally avoid matings with a figure above 6.25%."}
          </p>
          <div className={`csv-uploader-btn ${getButtonStyle(isValid, true)}`}>
            <ReactFileReader
              handleFiles={this.handleFileChange}
              fileTypes=".csv"
              multipleFiles={false}
            >
              <button type="button" className="btn btn--cerulean">
                Upload cow file csv
              </button>
            </ReactFileReader>
          </div>
          <div className={`csv-filename ${getButtonStyle(isValid, false)}`}>
            <p>Uploaded file</p>
            <div className="row container">
              <div>
                <ReactFileReader
                  handleFiles={this.handleFileChange}
                  fileTypes=".csv"
                  multipleFiles={false}
                >
                  <a>{_.truncate(fileName, { length: 25 })}</a>
                </ReactFileReader>
              </div>
              <button className="remove-csv-file" onClick={this.removeFile}>
                Remove
              </button>
            </div>
          </div>
          <p>
            {"You will need your cow file with cow number, "}
            {"cow sire and maternal grandsire to get started. See "}
            <CSVLink data={CsvHelper.getExampleCsv()} filename="example.csv">
              example file{" "}
            </CSVLink>
            <a
              id="example-csv-popover"
              className="example-csv-popover"
              onClick={this.showPopover}
            >
              <Octicon icon={Question} size="small" />
            </a>
            <Popover
              className="example-csv-popover"
              placement="bottom"
              isOpen={showPopover}
              target="example-csv-popover"
              toggle={this.showPopover}
            >
              <PopoverHeader className="popover-header">
                Example CSV file format
              </PopoverHeader>
              <PopoverBody>
                <ExampleCsvPopover />
              </PopoverBody>
            </Popover>
          </p>
          <i>
            * Note: Figures are calculated on known relationships for LIC bulls
            only.{" "}
          </i>
          <i>
            Accuracy of figures with missing or non-LIC ancestry may be lower.
          </i>
        </div>
        <div className={`error-messages container ${showErrorMessage(errors)}`}>
          <div className="row">
            <div className="main-message col-sm-11">
              {getErrorCountMessage(errors)}
            </div>
            <div className="collapse-icon col-sm-1">
              <button
                type="button"
                className="btn btn-link"
                onClick={this.toggle}
              >
                <Octicon icon={collapseIcon} size="small" />
              </button>
            </div>
          </div>
          <div>
            <Collapse isOpen={collapse}>
              <ul>{getErrorMessages(errors)}</ul>
            </Collapse>
          </div>
        </div>
      </div>
    );
  }

  private showPopover = () => {
    this.setState((prevState) => ({
      showPopover: !prevState.showPopover,
    }));
  };

  private toggle = () => {
    const { collapse } = this.state;
    this.setState({ collapse: !collapse });
  };

  private handleFileChange = (files: any) => {
    const { uploadCsv } = this.props;

    // check if csv file successfully loaded
    if (files.length > 0 && CsvHelper.isCsvFile(files[0])) {
      const reader = new FileReader();
      reader.readAsText(files[0]);
      reader.onload = () => {
        // convert csv data to cow object array
        const csvData: ICsvData[] = CsvHelper.csvToCows(reader.result);
        const errors = _.filter(
          csvData,
          (data) => data.validation === RowValidation.Error
        );
        const isValid =
          errors.length > 0 ? CsvValidation.Invalid : CsvValidation.Valid;

        uploadCsv(csvData, isValid);

        this.setState({
          csvData,
          fileName: files[0].name,
          isValid,
        });
      };
    } else {
      // error out if it is an obvious invalid file
      // eg: no file at all, no a csv type file, etc.
      const error: ICsvData = {
        id: 1,
        messages: ["Invalid CSV file - The file is not the CSV type"],
        validation: RowValidation.Error,
      };

      const csvData = [error];
      const isValid =
        files.length === 0 ? CsvValidation.Unknown : CsvValidation.Invalid;

      uploadCsv(csvData, isValid);

      this.setState({
        csvData,
        fileName: files.length === 0 ? "" : files[0].name,
        isValid,
      });
    }
  };
}

export default CsvUploader;



// WEBPACK FOOTER //
// ./src/scripts/components/CsvUploader.tsx